import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorState,
  LoadingState,
  PageHeader,
  StatPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowLeft,
  Building2,
  ClipboardCheck,
  FileText,
  FlaskConical,
  Hammer,
  Pencil,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import { deleteUnit } from '../../../api/buildings.js';
import { fetchUnit, fetchUnitActivity } from '../../../api/units.js';
import { ApiError } from '../../../lib/api-client.js';

import { ActivityTab } from './activity-tab.js';
import { ComplianceTab } from './compliance-tab.js';
import { DocumentsTab } from './documents-tab.js';
import { EditUnitSheet } from './edit-sheet.js';
import { InspectionsTab } from './inspections-tab.js';
import { LEAD_STATUS_LABELS, LEAD_STATUS_TONE, isLeadStatus } from './lead-status.js';
import { LeadTab } from './lead-tab.js';
import { OverviewTab } from './overview-tab.js';
import { WorkOrdersTab } from './work-orders-tab.js';

export function UnitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const isAdmin = meQuery.data?.user.role === 'admin';

  const detailQuery = useQuery({
    queryKey: ['unit', id],
    queryFn: () => fetchUnit(id!),
    enabled: Boolean(id),
  });
  const activityQuery = useQuery({
    queryKey: ['unit', id, 'activity'],
    queryFn: () => fetchUnitActivity(id!),
    enabled: Boolean(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteUnit(id!),
    onSuccess: () => {
      const buildingId = detailQuery.data?.building.id;
      if (buildingId) {
        void queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
        navigate(`/buildings/${buildingId}`);
      } else {
        navigate('/buildings');
      }
    },
    onError: (err) => {
      setDeleteError(err instanceof ApiError ? err.message : 'Could not archive unit');
    },
  });

  const headerMeta = useMemo(() => {
    if (!detailQuery.data) return null;
    const { unit, openWorkOrderCount, deviceCounts, documentCount } = detailQuery.data;
    const lead = isLeadStatus(unit.leadStatus) ? unit.leadStatus : null;
    return (
      <>
        <Badge variant="outline" className="capitalize">
          {unit.occupancyStatus.replace('_', ' ')}
        </Badge>
        {unit.unitType ? (
          <Badge variant="outline" className="capitalize">
            {unit.unitType.replace('_', ' ')}
          </Badge>
        ) : null}
        <StatPill
          tone={openWorkOrderCount > 0 ? 'warning' : 'neutral'}
          value={openWorkOrderCount}
          label={openWorkOrderCount === 1 ? 'open WO' : 'open WOs'}
        />
        <StatPill
          tone={deviceCounts.complianceTracked > 0 ? 'info' : 'neutral'}
          value={deviceCounts.complianceTracked}
          label={deviceCounts.complianceTracked === 1 ? 'tracked device' : 'tracked devices'}
        />
        <StatPill
          tone="neutral"
          value={documentCount}
          label={documentCount === 1 ? 'document' : 'documents'}
        />
        {lead ? (
          <StatPill tone={LEAD_STATUS_TONE[lead]} value={LEAD_STATUS_LABELS[lead]} withDot />
        ) : null}
      </>
    );
  }, [detailQuery.data]);

  if (detailQuery.isLoading) return <LoadingState rows={6} />;
  if (detailQuery.isError || !detailQuery.data) {
    return <ErrorState description="Could not load this unit." />;
  }

  const detail = detailQuery.data;
  const { unit, building } = detail;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Buildings', href: '/buildings' },
          { label: building.name, href: `/buildings/${building.id}` },
          { label: `Unit ${unit.unitNumber}` },
        ]}
        title={`Unit ${unit.unitNumber}`}
        description={
          <>
            {building.name} · {building.addressLine1}
            {unit.floor ? ` · Floor ${unit.floor}` : ''}
          </>
        }
        meta={headerMeta}
        actions={
          <>
            <Link to={`/buildings/${building.id}`}>
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4" /> Back to building
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
            {isAdmin ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm(`Archive unit ${unit.unitNumber}?`)) {
                    setDeleteError(null);
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="text-danger hover:text-danger"
              >
                <Trash2 className="h-4 w-4" /> Archive
              </Button>
            ) : null}
          </>
        }
      />

      {deleteError ? (
        <div className="mb-6">
          <ErrorState description={deleteError} />
        </div>
      ) : null}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <Building2 className="h-3.5 w-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="work-orders">
            <Hammer className="h-3.5 w-3.5" /> Work orders
          </TabsTrigger>
          <TabsTrigger value="inspections">
            <ShieldAlert className="h-3.5 w-3.5" /> Inspections
          </TabsTrigger>
          <TabsTrigger value="compliance">
            <ClipboardCheck className="h-3.5 w-3.5" /> Compliance
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="h-3.5 w-3.5" /> Documents
          </TabsTrigger>
          <TabsTrigger value="lead">
            <FlaskConical className="h-3.5 w-3.5" /> Lead
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="h-3.5 w-3.5" /> Activity
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          <OverviewTab detail={detail} />
        </TabsContent>
        <TabsContent value="work-orders" className="mt-4">
          <WorkOrdersTab unit={unit} building={building} />
        </TabsContent>
        <TabsContent value="inspections" className="mt-4">
          <InspectionsTab unit={unit} building={building} />
        </TabsContent>
        <TabsContent value="compliance" className="mt-4">
          <ComplianceTab unit={unit} building={building} />
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <DocumentsTab unit={unit} />
        </TabsContent>
        <TabsContent value="lead" className="mt-4">
          <LeadTab unit={unit} />
        </TabsContent>
        <TabsContent value="activity" className="mt-4">
          <ActivityTab
            entries={activityQuery.data?.entries ?? []}
            isLoading={activityQuery.isLoading}
          />
        </TabsContent>
      </Tabs>

      <EditUnitSheet open={editOpen} onOpenChange={setEditOpen} unit={unit} />
    </>
  );
}

export default UnitDetailPage;

// Tiny inline card so the empty-tab variants stay short.
export function EmptyTabCard({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="p-6 text-sm text-fg-secondary">{message}</CardContent>
    </Card>
  );
}
